import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Alert, Badge, Button, Drawer, Loader, Tooltip } from '@mantine/core'
import ReactMarkdown from 'react-markdown'
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import DocumentTitle from '@/components/ui/DocumentTitle'
import Table from '@/components/ui/table/Table'
import useApiSWR from '@/hooks/useApiSWR'
import dayjs from '@/lib/dayjs'

const formatDate = (value) => value ? new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(value)) : '—'
const money = (value) => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(value)

function safeUrl(value) {
    if (typeof value !== 'string') return null
    try {
        const url = new URL(value, 'https://baltic-watches.com')
        return ['https:', 'http:'].includes(url.protocol) ? url.href : null
    } catch {
        return null
    }
}

function TicketLink({ id }) {
    return id ? <a className="text-blue-700 underline" href={`https://balticwatches.gorgias.com/app/ticket/${id}`} target="_blank" rel="noopener noreferrer">Ticket #{id}</a> : null
}

function FrustrationBadge({ analysis }) {
    const levels = {
        unknown: { label: 'Indéterminée', count: 0, color: 'bg-gray-300' },
        none: { label: 'Aucune', count: 1, color: 'bg-green-500' },
        mild: { label: 'Modérée', count: 2, color: 'bg-amber-500' },
        high: { label: 'Élevée', count: 3, color: 'bg-red-500' },
    }
    const level = levels[analysis?.frustration] || levels.unknown
    const label = `Frustration : ${level.label.toLowerCase()}`
    return <Tooltip label={label} withArrow events={{ hover: true, focus: true, touch: true }}>
        <span tabIndex={0} role="img" aria-label={label} className="inline-flex items-center gap-1.5 py-2">
            {[0, 1, 2].map(index => <span key={index} aria-hidden="true" className={`h-2 w-2 rounded-full ${index < level.count ? level.color : 'bg-gray-200'}`} />)}
        </span>
    </Tooltip>
}

function LastActivity({ value }) {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 60000)
        return () => clearInterval(timer)
    }, [])
    const date = dayjs(value)
    if (!value || !date.isValid()) return '—'
    const recent = Math.abs(now - date.valueOf()) < 7 * 24 * 60 * 60 * 1000
    const label = recent ? date.locale('fr').from(dayjs(now)) : new Intl.DateTimeFormat('fr-FR', {
        timeZone: 'Europe/Paris', day: 'numeric', month: 'short', year: 'numeric',
    }).format(date.toDate())
    return <time dateTime={date.toISOString()} title={formatDate(value)}>{label.charAt(0).toUpperCase() + label.slice(1)}</time>
}

function MessageText({ text }) {
    return (text || '').split('[[split]]').map((bubble, index) => (
        <div key={index} className="whitespace-pre-wrap break-words [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
            <ReactMarkdown components={{ img: ({ alt }) => <span>{alt || 'Image'}</span>, a: ({ href, children }) => (
                <a href={safeUrl(href) || undefined} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">{children}</a>
            ) }}>{bubble.replaceAll('[[contact]]', '\nContact par email proposé.\n')}</ReactMarkdown>
        </div>
    ))
}

function MessageCard({ part }) {
    const items = part.data?.items || part.data?.pages
    if (Array.isArray(items) && ['products', 'sources', 'tracking'].includes(part.kind)) {
        return <div className="mt-3 grid gap-2">
            {(part.data?.order_number || part.data?.number) && <p className="text-sm font-medium">{part.data.order_number || part.data.number}</p>}
            {items.map((item, index) => <div key={index} className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
                {item.url ? <a href={safeUrl(item.url) || undefined} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">{item.name || item.title}</a> : <p>{item.name || item.title}</p>}
                {Array.isArray(item.lines) && item.lines.map((line, lineIndex) => <p key={lineIndex} className="mt-1 text-gray-600">{typeof line === 'string' ? line : `${line.label}: ${line.value}`}</p>)}
                {item.price_label && <p className="mt-1 text-gray-600">{item.price_label}</p>}
            </div>)}
            {part.data?.button?.url && <a href={safeUrl(part.data.button.url) || undefined} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 underline">{part.data.button.label || 'Voir le suivi'}</a>}
        </div>
    }
    if (part.kind === 'contact') return <p className="mt-2 text-sm text-gray-600">Formulaire de contact proposé</p>
    return <details className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm">
        <summary className="cursor-pointer">Carte affichée au client · {part.kind || part.name}</summary>
        <pre className="mt-2 whitespace-pre-wrap break-all text-xs">{JSON.stringify(part.data, null, 2)}</pre>
    </details>
}

function ConversationDetail({ id }) {
    const { data, error, isLoading, mutate } = useApiSWR(`/support/ai/conversations/${id}`, null, { refreshInterval: 30000 })
    if (error) return <Alert color="red" title="Conversation indisponible"><Button variant="subtle" onClick={() => mutate()}>Réessayer</Button></Alert>
    if (isLoading || !data) return <Loader aria-label="Chargement de la conversation" />
    return <div className="space-y-5">
        <section className="space-y-3 rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold">{data.ai_analysis?.subject || 'Analyse IA'}</h2>
            {data.ai_analysis && <>
                <div className="flex items-center gap-2 text-sm">Frustration estimée : <FrustrationBadge analysis={data.ai_analysis} /></div>
                {data.ai_analysis.frustration_reason && <p className="text-sm text-gray-600">{data.ai_analysis.frustration_reason}</p>}
            </>}
        </section>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            <p>Début : {formatDate(data.created_at)} · Dernier message : {formatDate(data.last_message_at)}</p>
            <p className="mt-2">Coût IA enregistré : <strong>{money(data.cost_usd)}</strong></p>
            {data.messages_with_incomplete_cost > 0 && <p className="mt-1 text-amber-800">Coût incomplet pour {data.messages_with_incomplete_cost} réponse(s).</p>}
            {data.gorgias_ticket_id ? <p className="mt-2"><TicketLink id={data.gorgias_ticket_id} /> · Formulaire envoyé le {formatDate(data.email_sent_at)}</p> : <p className="mt-2">Aucun ticket associé enregistré.</p>}
        </div>
        {data.messages.map((message) => {
            const parts = Array.isArray(message.parts) ? message.parts : []
            const hasText = parts.some(part => part.type === 'text' && part.value)
            return <article key={message.id} className={`rounded-xl border p-4 ${message.role === 'user' ? 'ml-6 border-blue-100 bg-blue-50' : 'mr-6 border-gray-200 bg-white'}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <strong className="text-sm text-gray-900">{message.role === 'user' ? 'Client' : 'Assistant IA'}</strong>
                    <time dateTime={message.created_at}>{formatDate(message.created_at)}</time>
                    {message.country && <span>{message.country}</span>}
                    {message.finish_reason === 'error' && <Badge color="red">Erreur de génération</Badge>}
                    {message.finish_reason === 'length' && <Badge color="orange">Réponse tronquée</Badge>}
                </div>
                {!hasText && <MessageText text={message.content} />}
                {parts.map((part, index) => part.type === 'text'
                    ? <MessageText key={index} text={part.value} />
                    : part.type === 'card' ? <MessageCard key={index} part={part} /> : null)}
                {message.role === 'assistant' && <div className="mt-3 flex flex-wrap gap-3 border-t border-gray-200 pt-2 text-xs text-gray-500">
                    {message.prompt_version_id != null && <Link className="underline" to={`/support/ai/policies/${message.prompt_version_id}`}>Agent v{message.prompt_version_id}</Link>}
                    <span>{message.model}</span>
                    {message.duration_ms != null && <span>{(message.duration_ms / 1000).toFixed(1)} s</span>}
                    {message.cost_usd != null && <span>{money(message.cost_usd)}</span>}
                </div>}
            </article>
        })}
    </div>
}

function StatChange({ current, previous, metric, period }) {
    const baseline = period === 'today' ? 'hier à la même heure' : 'la semaine dernière au même stade'
    const value = current[metric.key]
    const before = previous?.[metric.key]
    const incomplete = metric.key === 'cost_usd' && (current.incomplete_cost > 0 || previous?.incomplete_cost > 0)
    if (value == null || before == null || incomplete || (before === 0 && value !== 0 && !metric.points)) {
        return <p className="mt-2 text-xs text-gray-500" title={incomplete ? 'Coûts incomplets sur une des périodes' : `Pas de base comparable à ${baseline}`}>Évolution indisponible</p>
    }
    const delta = metric.points ? value - before : before === 0 ? 0 : (value - before) / before * 100
    const rounded = Math.round(delta * 10) / 10
    const color = metric.points && rounded !== 0 ? rounded > 0 ? 'text-red-700' : 'text-green-700' : 'text-gray-600'
    return <p className={`mt-2 text-xs tabular-nums ${color}`} title={`Par rapport à ${baseline}`}>
        {rounded > 0 ? '↑ +' : rounded < 0 ? '↓ ' : '→ '}{rounded.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}{metric.points ? ' pt' : ' %'}
        <span className="ml-1 text-gray-500">{period === 'today' ? 'vs hier' : 'vs sem. préc.'}</span>
    </p>
}

function ConversationStats({ data, error, retry }) {
    if (error) return <Alert color="red">Statistiques indisponibles. <Button variant="subtle" onClick={retry}>Réessayer</Button></Alert>
    const metrics = [
        { key: 'conversations', label: 'Conversations actives', value: period => period.conversations },
        { key: 'cost_usd', label: 'Coût des réponses', value: period => money(period.cost_usd) },
        { key: 'email_rate', points: true, label: 'Taux de bascule en email', value: period => period.email_rate == null ? '—' : `${period.email_rate.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`, detail: period => `${period.email_conversations} / ${period.conversations} conversations` },
    ]
    return <div className="border-b bg-white">
        <div className="grid grid-cols-1 divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
            {metrics.map(metric => <div key={metric.label} className="px-5 py-4">
                <p className="mb-3 text-sm text-gray-500">{metric.label}</p>
                <div className="grid grid-cols-2 gap-4">
                    {[['today', 'Aujourd’hui'], ['week', 'Cette semaine']].map(([key, label]) => <div key={key}>
                        <p className="text-xl font-medium tabular-nums">{data ? metric.value(data[key]) : '…'}</p>
                        <p className="mt-1 text-xs text-gray-500">{label}</p>
                        {data && <StatChange current={data[key]} previous={data[`previous_${key}`]} metric={metric} period={key} />}
                        {data && metric.detail && <p className="mt-1 text-xs text-gray-500">{metric.detail(data[key])}</p>}
                    </div>)}
                </div>
            </div>)}
        </div>
    </div>
}

export default function SupportConversationsPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { search } = useLocation()
    const stats = useApiSWR('/support/ai/conversations/stats', null, { refreshInterval: 30000 })
    const columns = [
        { key: 'subject', name: 'Sujet', display: 'mandatory', component: conversation => <div className="table-cell min-w-64 max-w-lg p-5">
            <button type="button" className="block w-full truncate text-left hover:underline" title={conversation.ai_analysis?.subject || conversation.preview || undefined} onClick={() => navigate(`/support/ai/conversations/${conversation.id}${search}`)}>{conversation.ai_analysis?.subject || conversation.preview || 'Conversation sans texte'}</button>
        </div> },
        { key: 'frustration', name: 'Frustration', display: 'optional_default', component: conversation => <div className="table-cell p-5"><FrustrationBadge analysis={conversation.ai_analysis} /></div> },
        { key: 'last_message_at', name: 'Dernière activité', display: 'optional_default', sortable: true, component: conversation => <div className="table-cell whitespace-nowrap p-5"><LastActivity value={conversation.last_message_at} /></div> },
        { key: 'created_at', name: 'Création', display: 'optional', sortable: true, component: conversation => <div className="table-cell whitespace-nowrap p-5">{formatDate(conversation.created_at)}</div> },
        { key: 'country', name: 'Pays', display: 'optional_default', component: conversation => <div className="table-cell p-5">{conversation.country || '—'}</div> },
        { key: 'messages', name: 'Messages échangés', display: 'optional_default', component: conversation => <div className="table-cell p-5">{Number(conversation.user_messages) + Number(conversation.assistant_messages) - Number(conversation.errors)}</div> },
        { key: 'tracking', name: 'Basculé en email', display: 'optional_default', component: conversation => <div className="table-cell p-5"><TicketLink id={conversation.gorgias_ticket_id} /></div> },
        { key: 'errors', name: 'Erreurs IA', display: 'optional', component: conversation => <div className="table-cell p-5">{conversation.errors > 0 ? <Badge color="red">{conversation.errors}</Badge> : '—'}</div> },
    ]

    return <>
        <DocumentTitle title="BO - Conversations IA - Baltic Watches" />
        <ScrollableScreen breadcrumbs={[{ label: 'Support IA' }, { label: 'Conversations' }]}>
            <Table
                className="h-full min-h-0"
                engineApiEndpoint="/support/ai/conversations"
                availableColumns={columns}
                searchable
                defaultSortColumn="last_message_at"
                getItemUrl={conversation => `/support/ai/conversations/${conversation.id}${search}`}
                topContent={<ConversationStats data={stats.data} error={stats.error} retry={() => stats.mutate()} />}
            />
        </ScrollableScreen>
        <Drawer opened={Boolean(id)} onClose={() => navigate(`/support/ai/conversations${search}`)} position="right" size="xl" title="Conversation IA · heures de Paris">
            {id && <ConversationDetail key={id} id={id} />}
        </Drawer>
    </>
}
